'use client';

import {CarouselItemFragment, CarouselSectionFieldsFragment,} from '@generated/graphql';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Container, Typography,} from '@mui/material';
import {ChevronLeft, ChevronRight} from '@mui/icons-material';
import {
  SLIDE_DURATION_MS,
  StyledCarouselCard,
  StyledCarouselChevronBtn,
  StyledCarouselContentHalf,
  StyledCarouselDot,
  StyledCarouselDots,
  StyledCarouselImageHalf,
  StyledCarouselInformationLinkButton,
  StyledCarouselSectionContainer,
  StyledCarouselSlideLayout,
  StyledCarouselTrack,
  StyledCarouselTrackSlide,
  StyledCarouselWrapper
} from '@/components/sections/carousel-section/Carousel.styles';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {isRenderableLinkButton} from '@/lib/ensure-link-button';
import ConditionalLinkButton from '@/components/ui/buttons/ConditionalLinkButton';
import RightArrowIcon from '@/components/ui/icons/RightArrow';
import {useReducedMotion} from '@/utils/useReducedMotion';

export type CarouselSectionFieldsWithActiveFields = CarouselItemFragment & {
    image: {
        url: string;
    };
};

export const carouselItemWithFieldsSet = (
  item: CarouselItemFragment | null
): item is CarouselSectionFieldsWithActiveFields => {
  return !!item?.image?.url;
};

type CarouselSectionProps = {
    section: CarouselSectionFieldsFragment;
    autoPlay?: boolean;
    interval?: number;
};

const SWIPE_THRESHOLD = 50;
// Number of slides cloned on each end of the track. 2 is enough so that the mobile
// peek / drag zone always has a neighbor visible while the user is on an edge clone.
const CLONE_COUNT = 2;

const CarouselSection: React.FC<CarouselSectionProps> = ({
  section,
  autoPlay = true,
  interval = 10000,
}) => {
  const slides = section.carouselItemsCollection?.items ?? [];
  const validSlides = slides.filter(carouselItemWithFieldsSet);

  return (
    <StyledCarouselSectionContainer {...section.id && { id: section.id }}>
      <Container
        maxWidth={'mdlg'}
        sx={(theme) => ({
          [theme.breakpoints.down('mobile')]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        })}
      >
        <CarouselBody
          key={validSlides.length}
          validSlides={validSlides}
          autoPlay={autoPlay}
          interval={interval}
        />
      </Container>
    </StyledCarouselSectionContainer>
  );
};

type CarouselBodyProps = {
  validSlides: CarouselSectionFieldsWithActiveFields[];
  autoPlay: boolean;
  interval: number;
};

// Keyed by validSlides.length in the parent — when the slide set changes, this
// component remounts with fresh state instead of needing an imperative reset.
const CarouselBody: React.FC<CarouselBodyProps> = ({validSlides, autoPlay, interval}) => {
  const slideCount = validSlides.length;
  const hasMultipleSlides = slideCount > 1;
  const reducedMotion = useReducedMotion();
  const autoPlayEnabled = autoPlay && !reducedMotion;

  // Extended track with CLONE_COUNT clones prepended and appended — so the mobile peek
  // and drag zone always have a neighbor rendered, even when the user is on an edge clone.
  // Track layout: [clone(-2), clone(-1), ...real, clone(0), clone(1)]
  const extendedSlides = useMemo(
    () => {
      if (!hasMultipleSlides) return validSlides;
      const at = (i: number) => validSlides[((i % slideCount) + slideCount) % slideCount];
      const head = Array.from({length: CLONE_COUNT}, (_, k) => at(slideCount - CLONE_COUNT + k));
      const tail = Array.from({length: CLONE_COUNT}, (_, k) => at(k));
      return [...head, ...validSlides, ...tail];
    },
    [validSlides, hasMultipleSlides, slideCount]
  );

  const [trackIndex, setTrackIndex] = useState(hasMultipleSlides ? CLONE_COUNT : 0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const realIndex = useMemo(() => {
    if (!hasMultipleSlides) return 0;
    return (((trackIndex - CLONE_COUNT) % slideCount) + slideCount) % slideCount;
  }, [trackIndex, slideCount, hasMultipleSlides]);

  const changeSlide = useCallback((newTrackIndex: number) => {
    if (newTrackIndex === trackIndex || slideCount === 0) return;
    setTrackIndex(newTrackIndex);
    setIsTransitioning(true);
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = setTimeout(() => {
      // Once the animation lands on a clone, snap to the equivalent real slide
      // in the same render as turning the transition off — the jump is invisible.
      if (newTrackIndex < CLONE_COUNT) {
        setTrackIndex(newTrackIndex + slideCount);
      } else if (newTrackIndex > slideCount + CLONE_COUNT - 1) {
        setTrackIndex(newTrackIndex - slideCount);
      }
      setIsTransitioning(false);
    }, SLIDE_DURATION_MS + 20);
  }, [trackIndex, slideCount]);

  const nextSlide = useCallback(() => {
    if (!hasMultipleSlides || isTransitioning) return;
    changeSlide(trackIndex + 1);
  }, [trackIndex, hasMultipleSlides, isTransitioning, changeSlide]);

  const prevSlide = useCallback(() => {
    if (!hasMultipleSlides || isTransitioning) return;
    changeSlide(trackIndex - 1);
  }, [trackIndex, hasMultipleSlides, isTransitioning, changeSlide]);

  const jumpToRealIndex = useCallback((targetRealIndex: number) => {
    if (!hasMultipleSlides || isTransitioning || targetRealIndex === realIndex) return;
    const forward = (targetRealIndex - realIndex + slideCount) % slideCount;
    const backward = (realIndex - targetRealIndex + slideCount) % slideCount;
    const newTrackIndex = forward <= backward
      ? trackIndex + forward
      : trackIndex - backward;
    changeSlide(newTrackIndex);
  }, [trackIndex, realIndex, slideCount, hasMultipleSlides, isTransitioning, changeSlide]);

  useEffect(() => {
    if (!autoPlayEnabled || !hasMultipleSlides) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoPlayEnabled, interval, hasMultipleSlides, nextSlide]);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, []);

  const trackRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  // Below md the card sizes to the active slide via --card-h so each slide's red area
  // hugs its own content. Desktop ignores the var (it has a fixed height in CSS).
  useLayoutEffect(() => {
    const active = slideRefs.current[trackIndex];
    const card = cardRef.current;
    if (!active || !card) return;
    const apply = () => card.style.setProperty('--card-h', `${active.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(active);
    return () => ro.disconnect();
  }, [trackIndex]);

  const setDragOffset = (px: number) => {
    if (trackRef.current) {
      trackRef.current.style.setProperty('--drag-px', `${px}px`);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Ignore new drags while a slide transition (or snap-back) is in flight —
    // otherwise isDragging and the animated transform can desync and leave the
    // track stuck mid-animation.
    if (!hasMultipleSlides || isTransitioning) return;
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    touchDeltaX.current = delta;
    setDragOffset(delta);
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null) return;
    const delta = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    setIsDragging(false);
    setDragOffset(0);
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta < 0) nextSlide();
      else prevSlide();
    } else {
      // No slide change — still animate the drag offset back to 0 smoothly.
      setIsTransitioning(true);
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = setTimeout(
        () => setIsTransitioning(false),
        SLIDE_DURATION_MS + 20
      );
    }
  };

  const renderSlideContent = (slide: CarouselSectionFieldsWithActiveFields) => (
    <StyledCarouselSlideLayout>
      <StyledCarouselContentHalf>
        {slide.heading && (
          <Typography variant='h3' component='p' gutterBottom sx={{fontWeight: 'bold'}}>
            {slide.heading}
          </Typography>
        )}
        {slide.breadText && (
          <Typography
            variant='body1'
            sx={(theme) => ({
              [theme.breakpoints.down('mobile')]: {display: 'none'},
            })}
          >
            {slide.breadText}
          </Typography>
        )}
        {isRenderableLinkButton(slide.linkButton) && (
          <ConditionalLinkButton
            button={slide.linkButton!}
            ButtonComponent={StyledCarouselInformationLinkButton}
            endIcon={<RightArrowIcon size={14} color='#f66' />}
          />
        )}
      </StyledCarouselContentHalf>
      <StyledCarouselImageHalf>
        <ContentfulImage
          src={slide.image.url}
          alt={slide.heading ?? ''}
          fill
          sizes='(max-width: 1100px) 100vw, 50vw'
          style={{ objectFit: 'cover' }}
          focalX={slide.focalX}
          focalY={slide.focalY}
          loading='eager'
        />
      </StyledCarouselImageHalf>
    </StyledCarouselSlideLayout>
  );

  return (
    <StyledCarouselWrapper>
      <StyledCarouselCard
        ref={cardRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <StyledCarouselTrack
          ref={trackRef}
          isTransitioning={isTransitioning}
          isDragging={isDragging}
          style={{'--current': trackIndex} as React.CSSProperties}
        >
          {extendedSlides.map((slide, i) => (
            <StyledCarouselTrackSlide
              key={`${slide.sys.id}-${i}`}
              ref={(el: HTMLDivElement | null) => { slideRefs.current[i] = el; }}
            >
              {renderSlideContent(slide)}
            </StyledCarouselTrackSlide>
          ))}
        </StyledCarouselTrack>
      </StyledCarouselCard>

      {hasMultipleSlides && (
        <>
          <StyledCarouselChevronBtn
            className='chevronBtn'
            onClick={prevSlide}
            sx={{ left: 16 }}
            aria-label='Previous slide'
          >
            <ChevronLeft />
          </StyledCarouselChevronBtn>

          <StyledCarouselChevronBtn
            className='chevronBtn'
            onClick={nextSlide}
            sx={{ right: 16 }}
            aria-label='Next slide'
          >
            <ChevronRight />
          </StyledCarouselChevronBtn>

          <StyledCarouselDots>
            {validSlides.map((_, index) => (
              <StyledCarouselDot
                key={index}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={realIndex === index ? 'true' : undefined}
                active={realIndex === index}
                onClick={() => jumpToRealIndex(index)}
              />
            ))}
          </StyledCarouselDots>
        </>
      )}
    </StyledCarouselWrapper>
  );
};

export default CarouselSection;
